import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { biometricSupported } from "@/lib/webauthn";
import { Loader2, Fingerprint } from "lucide-react";

export default function Login() {
  const { login, loginPasskey } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);

  const goto = (u) => navigate(u.role === "admin" || u.role === "manager" ? "/dashboard" : u.role === "barman" ? "/bar" : u.role === "fotografo" ? "/foto" : "/calendario");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(username, password, remember);
      goto(u);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const bioLogin = async () => {
    setError("");
    setBioLoading(true);
    try {
      const u = await loginPasskey(remember);
      goto(u);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail ? formatApiError(detail) : "Accesso biometrico non riuscito o annullato. Usa username e password.");
    } finally {
      setBioLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="w-full lg:w-[42%] flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-10 h-10 rounded-md bg-sky-700 grid place-items-center overflow-hidden p-1">
              <img src="/a20-travel-logo.png" alt="A20 TRAVEL" className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-slate-900">A20 MNG</div>
              <div className="text-xs text-slate-500">Guesthouse PMS · Maldive</div>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mb-1">Bentornato</h1>
          <p className="text-slate-500 text-sm mb-8">
            Accedi per gestire prenotazioni, proprietà e contabilità.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Username</label>
              <input
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none text-sm"
                placeholder="admin"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 rounded-md border border-slate-300 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none text-sm"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-slate-600 select-none cursor-pointer">
              <input data-testid="login-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="rounded border-slate-300" />
              Ricordami su questo dispositivo
            </label>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-sky-700 hover:bg-sky-800 text-white font-semibold py-2.5 rounded-md transition-colors duration-150 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Accedi
            </button>
            {biometricSupported() && (
              <button
                data-testid="login-biometric"
                type="button"
                onClick={bioLogin}
                disabled={bioLoading}
                className="w-full border border-sky-700 text-sky-700 hover:bg-sky-50 font-semibold py-2.5 rounded-md transition-colors duration-150 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {bioLoading ? <Loader2 size={16} className="animate-spin" /> : <Fingerprint size={18} />}
                Accedi con impronta / Face ID
              </button>
            )}
          </form>
        </div>
      </div>
      <div className="hidden lg:block relative flex-1">
        <img
          src="https://images.pexels.com/photos/9149367/pexels-photo-9149367.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
          alt="Maldives"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-heading text-2xl font-bold mb-2">
            Gestione ospiti & contabilità, semplificata.
          </h2>
          <p className="text-white/80 text-sm max-w-md">
            Calendario in tempo reale, prenotazioni per agenzia, report netto mensile.
          </p>
        </div>
      </div>
    </div>
  );
}
